var HeaderModule_1;
import { __decorate } from "tslib";
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, InjectionToken, NgModule, Optional } from '@angular/core';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { MessagesModule } from 'primeng/messages';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { EventType, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule, MsalRedirectComponent, MsalService } from '@azure/msal-angular';
import { TranslateLoader, TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { CheckIfPermissionMissing } from './pipes/check-if-permission-missing.pipe';
import { HeaderComponent } from './header.component';
import { msalConfig } from './model/auth-config';
import { ConstructorName, GeaUiLibConfiguration, SharedModule } from '../../shared';
import { BannerModule, ButtonV2Module, ComplexDialogV2Module, DialogV2Module, IconModule, LinkV2Module, SnackbarModule } from '../../basic-features';
import { EnvironmentTagComponent, VersionPopupComponent } from './environment-tag';
import { NotificationsV2Module } from '../notifications-v2';
import { TooltipModule } from 'primeng/tooltip';
import { VersionInterceptor } from '../../shared/interceptor/version.interceptor';
import { InactivityCountdownComponent } from './inactivity-countdown/inactivity-countdown.component';
import { ContactFormSideModalModule } from '../contact-form-sidemodal';
import { filter } from 'rxjs';
import { SystemStatusDialogComponent } from '../system-status-dialog';
import * as i0 from "@angular/core";
import * as i1 from "@ngx-translate/core";
import * as i2 from "../../shared";
import * as i3 from "@azure/msal-angular";
import * as i4 from "./services/login.service";
export class HeaderConfig {
  redirectUri = '';
  postLogoutRedirectUri = '';
  clientId = '';
  authority = '';
  authorityDomain = '';
  portalURL = '';
  secret = '';
  navigateToLoginRequestUrl = true;
  authConfigUrls = new Map();
  urls = {
    resources: '',
    permission: ''
  };
}
const testConfig = new HeaderConfig();
/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(config) {
  if (config) {
    msalConfig.auth.clientId = config.clientId;
    msalConfig.auth.postLogoutRedirectUri = config.postLogoutRedirectUri;
    msalConfig.auth.redirectUri = config.redirectUri;
    msalConfig.auth.authority = config.authority;
    msalConfig.auth.knownAuthorities = [config.authorityDomain];
    msalConfig.auth.navigateToLoginRequestUrl = config.navigateToLoginRequestUrl ?? true;
    if (config.authorityDomain.includes('localhost')) {
      msalConfig.auth.authorityMetadata = JSON.stringify({
        authorization_endpoint: config.authorityDomain + '/authorize',
        token_endpoint: config.authorityDomain + '/token'
      });
      msalConfig.auth.protocolMode = 'OIDC';
    }
  } else {
    // TODO: error handling
    // eslint-disable-next-line no-console
    console.log('MSALInstanceFactory config is null');
  }
  return new PublicClientApplication({
    ...msalConfig,
    system: {
      allowNativeBroker: false
    }
  });
}
/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory() {
  return {
    interactionType: InteractionType.Redirect
  };
}
export function MSALInterceptorConfigFactory(headerConfig, baseUrl = '') {
  if (!baseUrl) {
    console.warn('MSALInterceptorConfigFactory: baseUrl is not provided, msal interceptor will not work');
  }
  let protectedResourceMap = new Map();
  protectedResourceMap.set(baseUrl + 'asset/v1/public/*', null);
  protectedResourceMap.set(baseUrl + 'contact-service/v1/public-messages', null);
  protectedResourceMap.set(baseUrl + '*', ['openid', 'offline_access', 'email', headerConfig.clientId]);
  if (headerConfig.authConfigUrls) {
    protectedResourceMap = new Map([...protectedResourceMap, ...headerConfig.authConfigUrls]);
  }
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}
const translationFactory = http => {
  return new TranslateHttpLoader(http);
};
let HeaderModule = class HeaderModule {
  static {
    HeaderModule_1 = this;
  }
  translate;
  digitalPlatformOrgaAdminService;
  resourcesService;
  monitoringService;
  msalBroadcastService;
  loginService;
  static created = false;
  static forRoot(config) {
    if (HeaderModule_1.created) {
      throw new Error('HeaderModule NgModule.forRoot() used more than once');
    }
    this.created = true;
    return {
      ngModule: HeaderModule_1,
      providers: [{
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true
      }, {
        provide: testConfig,
        useValue: config
      }, {
        provide: HeaderConfig,
        useFactory: () => config instanceof InjectionToken ? inject(config) : config
      }, {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory,
        deps: [HeaderConfig]
      }, {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory
      }, {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory,
        deps: [HeaderConfig, [new Optional(), 'baseUrl']]
      }, {
        provide: 'portalLink',
        useFactory: config => config.portalURL,
        deps: [HeaderConfig]
      }, {
        provide: HTTP_INTERCEPTORS,
        useClass: VersionInterceptor,
        multi: true
      }, MsalService, MsalGuard, MsalBroadcastService, {
        provide: GeaUiLibConfiguration,
        useFactory: baseUrl => {
          if (!baseUrl) {
            console.warn('GeaUiLibConfiguration: baseUrl is not provided, backend requests will not work');
          }
          return new GeaUiLibConfiguration({
            basePath: baseUrl + 'geaid/v1'
          });
        },
        deps: [[new Optional(), 'baseUrl']],
        multi: false
      }]
    };
  }
  constructor(translate, digitalPlatformOrgaAdminService, resourcesService, monitoringService, msalBroadcastService, loginService, config) {
    this.translate = translate;
    this.digitalPlatformOrgaAdminService = digitalPlatformOrgaAdminService;
    this.resourcesService = resourcesService;
    this.monitoringService = monitoringService;
    this.msalBroadcastService = msalBroadcastService;
    this.loginService = loginService;
    if (config) {
      resourcesService.setUrl(config.urls?.resources);
      digitalPlatformOrgaAdminService.setApiVersion('1');
    } else {
      // TODO: error handling
      // eslint-disable-next-line no-console
      console.log('HeaderModule setup service urls not possible ');
    }
    this.msalBroadcastService.msalSubject$.pipe(filter(msg => msg.eventType === EventType.LOGIN_FAILURE)).subscribe(resp => {
      if (resp?.error?.message.includes('AADB2C90091')) {
        msalConfig.auth.navigateToLoginRequestUrl = true;
        const redirectRequest = {
          authority: msalConfig.auth.authority,
          scopes: ['openid']
        };
        this.loginService.login(redirectRequest);
      }
    });
    this.monitoringService.sendTrackEvent(Reflect.getMetadata('constructorName', HeaderModule_1), 'constructor');
    this.translate.setDefaultLang('en-US');
    const selectedLanguage = localStorage.getItem('language');
    this.translate.use(selectedLanguage ?? this.translate.defaultLang);
  }
  static ɵfac = function HeaderModule_Factory(t) {
    return new (t || HeaderModule)(i0.ɵɵinject(i1.TranslateService), i0.ɵɵinject(i2.DigitalPlatformOrganizationAdministrationService), i0.ɵɵinject(i2.ResourcesService), i0.ɵɵinject(i2.MonitoringService), i0.ɵɵinject(i3.MsalBroadcastService), i0.ɵɵinject(i4.LoginService), i0.ɵɵinject(HeaderConfig, 8));
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: HeaderModule,
    bootstrap: [HeaderComponent, MsalRedirectComponent]
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [TranslatePipe, provideHttpClient(withInterceptorsFromDi()), MessageService],
    imports: [CommonModule, IconModule, TooltipModule, MessageModule, MessagesModule, MsalModule, ButtonV2Module, NotificationsV2Module, SharedModule, TranslateModule.forRoot({
      extend: true,
      defaultLanguage: 'en-US',
      loader: {
        deps: [HttpClient],
        provide: TranslateLoader,
        useFactory: translationFactory
      }
    }), BannerModule, DialogV2Module, SnackbarModule, LinkV2Module, ContactFormSideModalModule, SharedModule, ComplexDialogV2Module, SystemStatusDialogComponent]
  });
};
HeaderModule = HeaderModule_1 = __decorate([ConstructorName('HeaderModule')], HeaderModule);
export { HeaderModule };
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HeaderModule, [{
    type: NgModule,
    args: [{
      declarations: [HeaderComponent, CheckIfPermissionMissing, EnvironmentTagComponent, VersionPopupComponent, InactivityCountdownComponent],
      imports: [CommonModule, IconModule, TooltipModule, MessageModule, MessagesModule, MsalModule, ButtonV2Module, NotificationsV2Module, SharedModule, TranslateModule.forRoot({
        extend: true,
        defaultLanguage: 'en-US',
        loader: {
          deps: [HttpClient],
          provide: TranslateLoader,
          useFactory: translationFactory
        }
      }), BannerModule, DialogV2Module, SnackbarModule, LinkV2Module, ContactFormSideModalModule, SharedModule, ComplexDialogV2Module, SystemStatusDialogComponent],
      providers: [TranslatePipe, provideHttpClient(withInterceptorsFromDi()), MessageService],
      exports: [HeaderComponent],
      bootstrap: [HeaderComponent, MsalRedirectComponent]
    }]
  }], () => [{
    type: i1.TranslateService
  }, {
    type: i2.DigitalPlatformOrganizationAdministrationService
  }, {
    type: i2.ResourcesService
  }, {
    type: i2.MonitoringService
  }, {
    type: i3.MsalBroadcastService
  }, {
    type: i4.LoginService
  }, {
    type: HeaderConfig,
    decorators: [{
      type: Optional
    }]
  }], null);
})();
